// form
import { useI18n } from 'iomart-hooks';
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { Box, TextField, TextFieldProps, Typography } from '@mui/material';
import { useState, useCallback } from 'react';

// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
  showError?: boolean;
};

export default function RHFHtmlEditor({ 
  name, 
  label, 
  helperText, 
  showError = true,
  rows = 8,
  ...other 
}: Props) {
  const { control } = useFormContext();
  const { t } = useI18n(false);
  const [htmlError, setHtmlError] = useState<string | null>(null);

  // Функция для расчета количества строк
  const calculateRows = useCallback((value: string) => {
    const minRows = Number(rows);
    if (!value) return minRows;
    const lineCount = value.split('\n').length;
    return Math.max(lineCount + 1, minRows);
  }, [rows]);

  const validateHtml = useCallback((value: string) => {
    if (!value || value.trim() === '') {
      setHtmlError(null);
      return true;
    }
    
    try {
      // Простая проверка на корректность HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(value, 'text/html');
      const errorNode = doc.querySelector('parsererror');
      
      if (errorNode) {
        setHtmlError('Некорректный HTML');
        return false;
      }
      
      setHtmlError(null);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Invalid HTML';
      setHtmlError(errorMessage);
      return false;
    }
  }, []);

  const formatHtml = useCallback((value: string) => {
    if (!value || value.trim() === '') {
      return '';
    }
    
    try {
      // Простое форматирование HTML
      return value
        .replace(/></g, '>\n<') // Разделяем теги
        .replace(/^\s+|\s+$/gm, '') // Убираем лишние пробелы
        .split('\n')
        .map((line, index, arr) => {
          const trimmed = line.trim();
          if (!trimmed) return '';
          
          // Определяем уровень вложенности
          const openTags = (value.substring(0, value.indexOf(trimmed)) || '').match(/<[^/][^>]*>/g) || [];
          const closeTags = (value.substring(0, value.indexOf(trimmed)) || '').match(/<\/[^>]*>/g) || [];
          const level = Math.max(0, openTags.length - closeTags.length);
          
          return '  '.repeat(level) + trimmed;
        })
        .filter(line => line)
        .join('\n');
    } catch {
      return value;
    }
  }, []);

  const handleBlur = useCallback((onBlur: () => void, value: string) => {
    validateHtml(value);
    onBlur();
  }, [validateHtml]);

  const handleChange = useCallback((onChange: (value: string) => void, value: string) => {
    validateHtml(value);
    onChange(value);
  }, [validateHtml]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box>
          <TextField
            {...field}
            label={t(label)}
            multiline
            rows={calculateRows(field.value || '')}
            sx={{
              '& .MuiOutlinedInput-input': {
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: '13px',
                lineHeight: 1.5,
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'text.disabled',
                opacity: 0.7,
              },
              '& .Mui-disabled .MuiInputBase-input::placeholder': {
                color: 'text.disabled',
                opacity: 0.5,
              },
            }}
            fullWidth
            value={field.value || ''}
            error={!!error || !!htmlError}
            helperText={
              error?.message || 
              htmlError || 
              helperText
            }
            size="small"
            InputLabelProps={{ shrink: true }}
            placeholder='<p>Введите HTML код...</p>'
            onBlur={(e) => handleBlur(field.onBlur, e.target.value)}
            onChange={(e) => handleChange(field.onChange, e.target.value)}
            {...other}
          />
          
          {!other.disabled && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              {field.value && !htmlError && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: 'primary.main' }
                  }}
                  onClick={() => {
                    const formatted = formatHtml(field.value);
                    field.onChange(formatted);
                  }}
                >
                  Форматировать HTML
                </Typography>
              )}
              
              {!field.value && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: 'primary.main' }
                  }}
                  onClick={() => {
                    const template = '<p>Введите описание товара...</p>';
                    field.onChange(template);
                  }}
                >
                  Вставить шаблон
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    />
  );
}